export interface BreadcrumbRoute {
  label: string;
  // true = top-level nav page (resets trail); false = drill-down (appends to trail)
  topLevel: boolean;
  // when true: acts as topLevel only if there are no search params; with params, treated as drill-down
  topLevelWhenNoParams?: boolean;
  // path of logical parent page — auto-injected when trail has no parent
  parent?: string;
}

// Maps URL path prefixes to route metadata.
// Longer prefixes must appear first for correct longest-prefix matching.
const BREADCRUMB_MAP: Record<string, BreadcrumbRoute> = {
  '/file-upload-logs': { label: 'File Upload Logs', topLevel: true },
  '/file-upload-status': { label: 'File Upload Status', topLevel: true },
  '/prod-stat-validate-list': { label: 'Prod Stat Prep', topLevel: true },
  '/prod-stat-dashboard': { label: 'Prod Stat Dashboard', topLevel: true },
  '/dollar-journal-dashboard': { label: 'Dollar Journal', topLevel: true },
  '/estimation-dashboard': { label: 'Estimation Factor Dashboard', topLevel: true, topLevelWhenNoParams: true, parent: '/prod-stat-validate-list' },
  '/estimation-details': { label: 'Estimation Details', topLevel: false },
  '/meterreaddetails': { label: 'Meter Read Details', topLevel: false },
  '/invoices-dashboard': { label: 'Invoices Dashboard', topLevel: true },
  '/nsp-core-info': { label: 'NSP Core Info', topLevel: true },
  '/vendor-list/new': { label: 'New Vendor', topLevel: false, parent: '/vendor-list' },
  '/vendor-list/edit': { label: 'Edit Vendor', topLevel: false, parent: '/vendor-list' },
  '/vendor-list': { label: 'Vendor List', topLevel: true },
  '/core-info/new': { label: 'Create Meter', topLevel: false },
  '/core-info': { label: 'Core Info', topLevel: false },
  '/audit': { label: 'Audit', topLevel: true },
  '/audit-summary': { label: 'Audit Summary', topLevel: true },
  '/aveva': { label: 'Aveva', topLevel: true },
  '/spp-reports-accounting': { label: 'Accounting Report', topLevel: true },
  '/spp-reports-invoice-amounts': { label: 'Invoice Amounts Report', topLevel: true },
  '/spp-reports-prod-stat-netgen': { label: 'Prod Stat Generation Amounts Report', topLevel: true },
  '/spp-reports-post-prod-stat-netgen': { label: 'Post Prod Stat Generation Amounts Report', topLevel: true },
  '/data-migration': { label: 'SPP Data Migration', topLevel: true },
  '/oati/core-info/vendor-info': { label: 'Vendor Info', topLevel: true },
  '/oati/core-info/peak-config': { label: 'Peak Configuration', topLevel: false },
  '/oati/core-info/point-to-point': { label: 'Point-to-Point Rates', topLevel: false },
  '/oati/core-info/network': { label: 'Network Rates', topLevel: false },
  '/oati/core-info/rates': { label: 'Rates', topLevel: true },
  '/oati/dashboard': { label: 'Reservation Data', topLevel: true },
  '/oati/computation': { label: 'QC Computation', topLevel: false },
  '/oati/validation': { label: 'Validation', topLevel: false },
  '/oati/invoice': { label: 'OATT Invoice', topLevel: true },
  '/oati/nsp-core-info': { label: 'NSP Core Info', topLevel: true },
  '/oati/audit': { label: 'Audit', topLevel: true },
  '/oati/revision': { label: 'Revision', topLevel: false, parent: '/oati/dashboard' },
  '/oati/upload': { label: 'Upload', topLevel: false },
};

// Sort keys by descending length so longest prefix wins.
const SORTED_KEYS = Object.keys(BREADCRUMB_MAP).sort((a, b) => b.length - a.length);

export function getRouteInfo(pathname: string): BreadcrumbRoute | null {
  for (const key of SORTED_KEYS) {
    if (pathname === key || pathname.startsWith(key + '/')) {
      return BREADCRUMB_MAP[key];
    }
  }
  return null;
}

// Legacy helper kept for tests that import getBreadcrumb directly.
export function getBreadcrumb(pathname: string): { section: string; page: string } | null {
  const info = getRouteInfo(pathname);
  if (!info) return null;
  return { section: '', page: info.label };
}
